import React, { CSSProperties, ReactElement, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DEFAULT_NS } from 'appConstants/translationNamespaces';
import { baseColors } from 'appConstants';
import { Button } from 'shared-components/Button';
import { ReactComponent as EmailMarketingIcon } from 'assets/icons/email-marketing.svg';
import { COMO_ERROR_KEYS } from './translationKeys';

interface Props {
  error: string | null;
  retryAt: number | null;
  // Without it there is nothing to retry: the screen only reports the state of the merchant link.
  onOpen?: () => void;
  // Overrides for screens that are not the migration notice (title) or not a retry (button).
  titleKey?: string;
  actionKey?: string;
}

// Mirrors the error-pages layout (components/error-pages/styles) with inline styles: importing those
// styled-components, or adding new ones, shifts generated class names in unrelated snapshots.
const containerStyle: CSSProperties = {
  display: 'flex',
  height: '60vh',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center'
};
const iconStyle: CSSProperties = { margin: '0 0 15px 0' };
const titleStyle: CSSProperties = { fontWeight: 'bold', fontSize: 24, margin: '0 0 15px 0' };
// Mirrors NotificationBox (error): coloured bar with an icon on the left, at the top of the page content.
const errorBoxStyle: CSSProperties = {
  position: 'relative',
  maxWidth: 600,
  minHeight: 50,
  margin: '20px auto 0',
  padding: 15,
  boxSizing: 'border-box',
  background: '#fff',
  textAlign: 'left',
  border: `1px solid ${baseColors.red}`,
  borderLeftWidth: 50
};
const errorIconStyle: CSSProperties = {
  position: 'absolute',
  top: -1,
  bottom: 0,
  left: -51,
  width: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 20
};
const actionStyle: CSSProperties = { marginTop: 30 };

const useCoolingDown = (retryAt: number | null): boolean => {
  const [, tick] = useState(0);
  const remaining = retryAt === null ? 0 : retryAt - Date.now();

  useEffect(() => {
    if (remaining <= 0) return undefined;
    const timer = setTimeout(() => tick(count => count + 1), remaining);
    return () => clearTimeout(timer);
  }, [retryAt, remaining > 0]);

  return remaining > 0;
};

export const ComoHandOff = ({ error, retryAt, onOpen, titleKey, actionKey }: Props): ReactElement => {
  const { t } = useTranslation(DEFAULT_NS);
  const coolingDown = useCoolingDown(retryAt);

  return (
    <div data-testid="como-handoff">
      {error && (
        <div style={errorBoxStyle}>
          <div style={errorIconStyle}>
            <i className="fa fa-times-circle" />
          </div>
          <div role="alert">{t(COMO_ERROR_KEYS[error] || COMO_ERROR_KEYS.UNAVAILABLE)}</div>
        </div>
      )}
      <div style={containerStyle}>
        <div>
          <div style={iconStyle}>
            <EmailMarketingIcon width={64} height={59} />
          </div>
          <div style={titleStyle}>{t(titleKey || 'comoHandOffTitle')}</div>
          {onOpen && (
            <div style={actionStyle}>
              <Button primary data-testid="como-open" disabled={coolingDown} onClick={onOpen}>
                {t(actionKey || (error ? 'comoHandOffRetry' : 'comoHandOffOpen'))}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
